/**
 * Alert Dialog
 *
 * A modal dialog for showing alerts/errors, replacing native browser alert().
 */

import { Button } from './button';
import { X, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { createPortal } from 'react-dom';

interface AlertDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type?: 'info' | 'success' | 'error';
  closeLabel?: string;
}

export function AlertDialog({
  isOpen,
  onClose,
  title,
  message,
  type = 'info',
  closeLabel = 'OK',
}: AlertDialogProps) {
  if (!isOpen) return null;

  const icons = {
    info: <Info className="w-6 h-6 text-primary-500" />,
    success: <CheckCircle className="w-6 h-6 text-green-500" />,
    error: <AlertCircle className="w-6 h-6 text-red-500" />,
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative bg-white dark:bg-surface-900 rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 dark:border-surface-700">
          <div className="flex items-center gap-3">
            {icons[type]}
            <h3 className="text-lg font-semibold text-surface-900 dark:text-white">
              {title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-surface-500 hover:text-surface-700 dark:hover:text-surface-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-surface-600 dark:text-surface-400 whitespace-pre-wrap">
            {message}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end px-6 py-4 bg-surface-50 dark:bg-surface-800">
          <Button onClick={onClose}>
            {closeLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
